import axios, {
  AxiosError,
  AxiosProgressEvent,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { API_ENDPOINTS, PASSWORD_CHANGE_REQUIRED } from "../constants/apiConstants";
import { REACT_APP_API_URL } from "../constants/envConstants";
import {
  getAccessToken,
  getRefershToken,
  setAuthTokens,
} from "../helpers/authHelpers";
import { AppDispatch } from "./index";
import { logoutUser, requirePasswordChange } from "./user/userSlice";
import { openTermsGate, resetTerms } from "./terms/termsSlice";
import { getTermsGateDetail } from "./terms/termsGateError";

interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  useResponeInterceptor?: boolean;
  isRefreshRequest?: boolean;
  _retry?: boolean;
  sentAccessToken?: string;
}

let interceptorsInitialized = false;
let refreshPromise: Promise<void> | null = null;

const REFRESH_LOCK_KEY = "vendor-token-refresh";

/**
 * Runs a single token refresh. Serialized across browser tabs via the Web
 * Locks API so two tabs never POST the same (rotating) refresh token — which
 * the backend rejects with "revoked or rotated". Inside the lock we re-read the
 * stored refresh token: if another tab already rotated it, we skip the network
 * call and let the caller retry with the freshly stored access token.
 */
const runRefresh = async (): Promise<void> => {
  const refreshTokenBefore = getRefershToken();

  const doRefresh = async () => {
    const currentRefreshToken = getRefershToken();
    if (currentRefreshToken && currentRefreshToken !== refreshTokenBefore) {
      return;
    }
    const response = await refreshTokenRequest();
    if (!isApiSuccess(response.status)) {
      throw new Error("Token refresh failed");
    }
    const { tokens } = response.data.data;
    setAuthTokens(tokens);
  };

  const locks = (navigator as Navigator & { locks?: LockManager })?.locks;
  if (locks?.request) {
    await locks.request(REFRESH_LOCK_KEY, doRefresh);
  } else {
    await doRefresh();
  }
};

const isRefreshUrl = (url?: string) =>
  !!url &&
  (url.includes(API_ENDPOINTS.REFRESH_TOKEN) || url.includes("refresh-auth/vendor"));

/** Login / password flows — 401 is invalid credentials, not an expired access token. */
const isPublicAuthUrl = (url?: string) =>
  !!url &&
  (url.includes(API_ENDPOINTS.LOGIN) ||
    url.includes("auth/login/vendor") ||
    url.includes(API_ENDPOINTS.FORGOT_PASSWORD()) ||
    url.includes(API_ENDPOINTS.RESET_PASSWORD()));

/**
 * Change-password checks the current password, so its 401 means an expired
 * temporary password — not an expired session. It still sends the vendor's
 * token, unlike the public auth URLs above.
 */
const isPasswordCheckUrl = (url?: string) =>
  !!url && url.includes(API_ENDPOINTS.CHANGE_PASSWORD);

const isPasswordChangeRequired = (error: AxiosError) =>
  error.response?.status === 403 &&
  (error.response.data as { data?: { reason?: string } })?.data?.reason ===
    PASSWORD_CHANGE_REQUIRED;

export const axiosInstance = axios.create({
  baseURL: REACT_APP_API_URL,
  responseType: "json",
  headers: {
    "Content-Type": "application/json",
  },
});

export const setAxiosInterceptors = (dispatch: AppDispatch) => {
  if (interceptorsInitialized) {
    return;
  }
  interceptorsInitialized = true;

  axiosInstance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const customConfig = config as CustomAxiosRequestConfig;
      const isRefresh =
        customConfig.isRefreshRequest || isRefreshUrl(config.url);
      const isPublicAuth = isPublicAuthUrl(config.url);

      if (!isRefresh && !isPublicAuth) {
        const accessToken = getAccessToken();
        customConfig.useResponeInterceptor =
          !!getRefershToken() || !!accessToken;
        customConfig.sentAccessToken = accessToken;
        if (accessToken) {
          config.headers.Authorization = accessToken;
        }
      } else {
        customConfig.useResponeInterceptor = false;
      }

      return config;
    },
    (error: AxiosError) => Promise.reject(error)
  );

  axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as CustomAxiosRequestConfig | undefined;

      if (!error.response || !originalRequest) {
        return Promise.reject(error);
      }

      const { status } = error.response;
      const storedRefreshToken = getRefershToken();

      // The terms gate. Authorization, not authentication: the vendor's session
      // is fine, they simply have not accepted the current T&Cs. Recoverable by
      // accepting, so this must NOT take the 401 logout path.
      //
      // Switch on the backend constant, never the message text — it is a
      // constant precisely so clients can branch on it. Note the accept
      // endpoint's own 403 carries no `data.reason`, so it falls through here.
      // A vendor still on the temporary password. Like the terms gate this is
      // authorization, not a dead session, so it must not log out.
      if (isPasswordChangeRequired(error)) {
        dispatch(requirePasswordChange());
        return Promise.reject(error);
      }

      const termsGate = getTermsGateDetail(error);

      if (termsGate) {
        dispatch(openTermsGate(termsGate));

        // Reject without retrying. The calling thunk still fails and the vendor
        // re-triggers the action after accepting — an automatic retry after an
        // unknown delay risks double-submitting a bid.
        return Promise.reject(error);
      }

      if (
        status === 401 &&
        !originalRequest._retry &&
        originalRequest.useResponeInterceptor &&
        storedRefreshToken &&
        !isRefreshUrl(originalRequest.url) &&
        !isPublicAuthUrl(originalRequest.url) &&
        !isPasswordCheckUrl(originalRequest.url)
      ) {
        originalRequest._retry = true;

        // Another request already refreshed the token while this one was in
        // flight. Don't refresh again — just retry with the current token.
        if (!refreshPromise) {
          const currentAccessToken = getAccessToken();
          if (
            currentAccessToken &&
            currentAccessToken !== originalRequest.sentAccessToken
          ) {
            return axiosInstance(originalRequest);
          }
        }

        if (!refreshPromise) {
          refreshPromise = runRefresh()
            .catch((refreshError) => {
              // The server actively rejected the refresh (invalid / revoked /
              // rotated — any 4xx), or the refresh token is gone: the session
              // cannot continue, so log the user out. Do NOT log out on a
              // transient network error (no response), to avoid kicking users
              // out on flaky connections.
              const hasServerResponse = !!(refreshError as AxiosError)?.response;
              if (hasServerResponse || !getRefershToken()) {
                dispatch(logoutUser());
                // A stale terms.viewerMode surviving this forced logout would
                // reopen the blocking viewer for whoever logs into this tab
                // next, before their own session has fetched anything.
                dispatch(resetTerms());
              }
              throw refreshError;
            })
            .finally(() => {
              refreshPromise = null;
            });
        }

        return refreshPromise.then(() => axiosInstance(originalRequest));
      }

      if (
        status === 401 &&
        !isPublicAuthUrl(originalRequest.url) &&
        !isPasswordCheckUrl(originalRequest.url)
      ) {
        const shouldLogout =
          !storedRefreshToken ||
          originalRequest._retry ||
          isRefreshUrl(originalRequest.url);

        if (shouldLogout) {
          dispatch(logoutUser());
          dispatch(resetTerms());
        }
      }

      return Promise.reject(error);
    }
  );
};

export enum API_METHOD_TYPE {
  GET = "GET",
  POST = "POST",
  UPDATE = "UPDATE",
  DELETE = "DELETE",
  PUT = "PUT",
  PATCH = "PATCH",
}

type BodyType = Record<string, any> | Array<any> | FormData | null;

export const isApiSuccess = (status: number) => status >= 200 && status <= 299;

export const apiManager = async (
  url: string,
  body: BodyType,
  method: AxiosRequestConfig["method"],
  header: AxiosRequestConfig["headers"] = {},
  baseURL = REACT_APP_API_URL,
  _retry: boolean = false,
  onUploadProgress?: (progressEvent: AxiosProgressEvent) => void,
  signal?: AbortSignal,
  isRefreshRequest: boolean = false
): Promise<AxiosResponse> => {
  const defaultHeaders = {
    ...axiosInstance.defaults.headers.common,
    ...header,
  };
  const requestObject = {
    method: method,
    url: url,
    data: body,
    baseURL: baseURL,
    headers: defaultHeaders,
    _retry,
    onUploadProgress,
    signal,
    isRefreshRequest,
  };
  return axiosInstance(requestObject);
};

const refreshTokenRequest = async () => {
  const body = { refreshToken: getRefershToken() };
  return apiManager(
    API_ENDPOINTS.REFRESH_TOKEN,
    body,
    API_METHOD_TYPE.POST,
    {},
    undefined,
    false,
    undefined,
    undefined,
    true
  );
};
